/**
 * One round's results on the Pick'em Results tab (ISSUE-1305, ISSUE-1309):
 * a card per member, everyone's picks game by game. Visibility, outcomes,
 * points, ranks, round winners and the tiebreaker difference are all server
 * fields on `/pickem/results-get`; this file lays them out.
 */
import { usePickemResultsGet } from "@sportsmet/contest-sdk/private";
import { cn, InlineBanner, Select, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { type typeTeamName, useTeamNames } from "../pick/useTeamNames";
import {
  PICKEM_RESULTS_EMPTY,
  PICKEM_RESULTS_EVERYONE,
  PICKEM_RESULTS_FAILED,
  PICKEM_RESULTS_HIDDEN,
  PICKEM_RESULTS_LOADING,
  PICKEM_RESULTS_MEMBER_LABEL,
  PICKEM_RESULTS_MISSED,
  PICKEM_RESULTS_NO_PICK,
  PICKEM_RESULTS_ROUND_LABEL,
  PICKEM_RESULTS_ROUND_WINNER,
  PICKEM_RESULTS_YOU,
  PICKEM_RETRY,
  pickemActualTotalLine,
  pickemMissedLine,
  pickemPointsLabel,
  pickemRankLabel,
  pickemRecordLabel,
  pickemRoundRankLabel,
  pickemTiebreakerGuessLine,
} from "./pickemCopy";
import {
  PICKEM_TONE_CLASS,
  pickemOutcomeLabel,
  pickemPickLineLabel,
  pickemShortGameLabel,
  pickemTeam,
  type typePickemTone,
} from "./pickemFormat";
import type {
  typePickemResultsGame,
  typePickemResultsPick,
  typePickemResultsRow,
} from "./pickemTypes";

const EVERYONE = "everyone";

type typePickChip = { tone: typePickemTone; label: string };

export function pickemResultsPickChip(
  pick: typePickemResultsPick,
): typePickChip {
  if (pick.visibility === "hidden") {
    return { tone: "none", label: PICKEM_RESULTS_HIDDEN };
  }
  if (pick.visibility === "noPick") {
    return {
      tone: pick.isMissed ? "loss" : "none",
      label: pick.isMissed ? PICKEM_RESULTS_MISSED : PICKEM_RESULTS_NO_PICK,
    };
  }
  const outcome = pick.outcome ?? "pending";
  return { tone: outcome, label: pickemOutcomeLabel(outcome) };
}

/**
 * "OKC +4.5", the picked team with its frozen line when there is one. The
 * pick's `spreadLine` is already the picked side's number, so an away pick
 * keeps its sign (ISSUE-1305).
 */
export function pickemPickedTeamLabel(input: {
  pick: typePickemResultsPick;
  teamsById: ReadonlyMap<string, typeTeamName>;
}): string | null {
  const teamId = input.pick.teamId ?? null;
  if (input.pick.visibility !== "visible" || teamId === null) return null;
  const team = pickemTeam(input.teamsById, teamId).abbreviation;
  const line = pickemPickLineLabel(input.pick.spreadLine);
  return line === null ? team : `${team} ${line}`;
}

const PickRow = (props: {
  pick: typePickemResultsPick;
  game: typePickemResultsGame | undefined;
  teamsById: ReadonlyMap<string, typeTeamName>;
}): ReactNode => {
  const chip = pickemResultsPickChip(props.pick);
  const picked = pickemPickedTeamLabel(props);
  const rank = props.pick.confidenceRank ?? null;
  return (
    <li
      className="flex items-center gap-2 py-1"
      data-testid={`pickem-result-pick-${props.pick.eventId}`}
    >
      <span className="w-24 shrink-0 truncate text-caption text-ink-secondary">
        {props.game === undefined
          ? ""
          : pickemShortGameLabel({
              teamsById: props.teamsById,
              awayTeamId: props.game.awayTeamId,
              homeTeamId: props.game.homeTeamId,
            })}
      </span>
      {/* The rank wraps under the team on a narrow phone instead of being
          cut off (ISSUE-1305). */}
      <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1 text-caption text-ink">
        <span className="whitespace-nowrap">{picked ?? ""}</span>
        {rank !== null && picked !== null ? (
          <span
            className="whitespace-nowrap text-ink-secondary"
            data-testid="pickem-result-rank"
          >
            · {pickemRankLabel(rank)}
          </span>
        ) : null}
      </span>
      <span
        className={cn(
          "shrink-0 rounded-full px-2 text-caption",
          PICKEM_TONE_CLASS[chip.tone],
        )}
        data-tone={chip.tone}
      >
        {chip.label}
      </span>
      <span className="w-12 shrink-0 text-right text-caption text-ink tabular-nums">
        {typeof props.pick.points === "number" &&
        props.pick.visibility === "visible"
          ? pickemPointsLabel(props.pick.points)
          : ""}
      </span>
    </li>
  );
};

const MemberCard = (props: {
  row: typePickemResultsRow;
  gamesById: ReadonlyMap<string, typePickemResultsGame>;
  teamsById: ReadonlyMap<string, typeTeamName>;
}): ReactNode => {
  const { row } = props;
  const facts = [pickemRecordLabel(row)];
  if (row.missed > 0) facts.push(pickemMissedLine(row.missed));
  return (
    <article
      className={cn(
        "flex flex-col gap-2 rounded-md bg-surface p-3",
        row.isViewer ? "border-2 border-primary" : "border border-hairline",
      )}
      data-testid="pickem-result-row"
      data-user-id={row.userId}
    >
      <div className="flex items-center gap-2">
        {typeof row.roundRank === "number" ? (
          <span className="w-8 shrink-0 text-body-strong text-ink tabular-nums">
            {pickemRoundRankLabel(row.roundRank)}
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate text-body-strong text-ink">
          {row.displayName}
          {row.isViewer ? (
            <span className="text-ink-secondary"> ({PICKEM_RESULTS_YOU})</span>
          ) : null}
        </span>
        <span className="shrink-0 text-body-strong text-ink tabular-nums">
          {pickemPointsLabel(row.points)}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-caption text-ink-secondary">
        {row.isRoundWinner ? (
          <span
            className="rounded-full border border-success bg-success-tint px-2 font-semibold text-ink"
            data-testid="pickem-round-winner"
          >
            {PICKEM_RESULTS_ROUND_WINNER}
          </span>
        ) : null}
        <span className="tabular-nums">{facts.join(" · ")}</span>
        {typeof row.tiebreakerGuess === "number" ? (
          <span className="tabular-nums" data-testid="pickem-result-guess">
            {pickemTiebreakerGuessLine({
              guess: row.tiebreakerGuess,
              diff: row.tiebreakerDiff ?? null,
            })}
          </span>
        ) : null}
      </div>
      <ul className="flex flex-col">
        {row.picks.map((pick) => (
          <PickRow
            key={pick.eventId}
            pick={pick}
            game={props.gamesById.get(pick.eventId)}
            teamsById={props.teamsById}
          />
        ))}
      </ul>
    </article>
  );
};

export const PickemRoundResults = ({
  poolId,
}: {
  poolId: string;
}): ReactNode => {
  // The filters are server lists; null asks the server for its own default
  // (the current round, everyone).
  const [roundId, setRoundId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const query = usePickemResultsGet({ poolId, roundId, userId });
  const data = query.data?.data ?? null;
  const { teamsById } = useTeamNames({ poolId });

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-3" data-testid="pickem-results-loading">
        <Skeleton shape="card" label={PICKEM_RESULTS_LOADING} />
        <Skeleton shape="card" label={PICKEM_RESULTS_LOADING} />
      </div>
    );
  }
  if (query.isError || data === null) {
    return (
      <div data-testid="pickem-results-failed">
        <InlineBanner
          variant="warning"
          message={PICKEM_RESULTS_FAILED}
          action={{
            label: PICKEM_RETRY,
            onAction: () => {
              void query.refetch();
            },
          }}
        />
      </div>
    );
  }

  const gamesById = new Map(data.games.map((game) => [game.eventId, game]));

  return (
    <div className="flex flex-col gap-3" data-testid="pickem-round-results">
      <div className="flex flex-col gap-3 md:flex-row">
        <Select
          label={PICKEM_RESULTS_ROUND_LABEL}
          items={data.filters.rounds.map((round) => ({
            value: round.roundId,
            label: round.label,
          }))}
          value={data.round.roundId}
          onValueChange={(next) => {
            setRoundId(next);
          }}
          className="flex-1"
        />
        <Select
          label={PICKEM_RESULTS_MEMBER_LABEL}
          items={[
            { value: EVERYONE, label: PICKEM_RESULTS_EVERYONE },
            ...data.filters.members.map((member) => ({
              value: member.userId,
              label: member.displayName,
            })),
          ]}
          value={userId ?? EVERYONE}
          onValueChange={(next) => {
            setUserId(next === EVERYONE ? null : next);
          }}
          className="flex-1"
        />
      </div>
      {typeof data.actualTiebreakerTotal === "number" ? (
        <p
          className="text-caption text-ink-secondary"
          data-testid="pickem-actual-total"
        >
          {pickemActualTotalLine(data.actualTiebreakerTotal)}
        </p>
      ) : null}
      {data.rows.length === 0 ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="pickem-results-empty"
        >
          {PICKEM_RESULTS_EMPTY}
        </p>
      ) : (
        data.rows.map((row) => (
          <MemberCard
            key={row.entryId}
            row={row}
            gamesById={gamesById}
            teamsById={teamsById}
          />
        ))
      )}
    </div>
  );
};
